import { EditablePreviewWorld, Vec3 } from '../types'
import { FRAME_SECONDS } from './constants'
import { BOSS_BGM_URL, CLEAR_BGM_URL } from './missionBgm'
import { pickRandom } from './primitives/pickRandom'
import {
  findIdByName,
  findStateByName,
  nextTempId,
  showHudText,
  touchesAvatar,
} from './sharedHelpers'
import {
  BehaviorContext,
  BehaviorHandler,
  MissionBehaviors,
  HudStyle,
} from './types'

/**
 * RJF01 のミッション環境ビヘイビア。
 *
 * テンプレート rbxl（src/missions/templates/RJF01.rbxl）に埋め込まれた Roblox
 * スクリプト（黄色い床の落下・速度リセット・ボス AI・攻撃ブロック・クリア判定）を
 * TypeScript へ移植したもの。元 Lua の semantics に挙動を合わせる。
 *
 * Roblox の `wait()`（引数なし）は約 1/30 秒。ここでも同値を 1 フレームとして扱う。
 */

/** Fall（黄色い床）が透明になる速度（Lua: speed = 0.01 / フレーム）。 */
const FALL_TRANSPARENCY_STEP = 0.01

/** Fall が落ちて（非衝突に）から復帰するまでの待機秒数（Lua: wait(3)）。 */
const FALL_RECOVER_SECONDS = 3

/** ResetSpeed 接触時に戻す歩行速度（Lua: humanoid.WalkSpeed = 16）。 */
const RESET_WALK_SPEED = 16

/** ボス AI の 1 サイクル待機秒数（Lua: wait(3)）。 */
const BOSS_CYCLE_SECONDS = 3

/** ボスのテレポート候補 X のオフセット（Lua: {posX-10, posX, posX+10}）。 */
const BOSS_X_OFFSETS: ReadonlyArray<number> = [-10, 0, 10]

/** Attack ブロックの寸法（rbxl 実測: Size = 5,5,5）。 */
const ATTACK_SIZE: Vec3 = { x: 5, y: 5, z: 5 }

/** Attack ブロックの色（rbxl 実測: Color = 1,0,0 = 純赤）。 */
const ATTACK_COLOR = '#FF0000'

/** Attack 生成位置の boss PrimaryPart からのオフセット（Lua: +Vector3.new(0,2,5)）。 */
const ATTACK_SPAWN_OFFSET: Vec3 = { x: 0, y: 2, z: 5 }

/** Attack ブロックの自壊までの秒数（Lua: DestroyScript wait(3)）。 */
const ATTACK_LIFETIME_SECONDS = 3

/** Attack が 1 フレームに進む +Z 量（Lua: Position += Vector3.new(0,0,1)）。 */
const ATTACK_STEP_Z = 1

/** 火の粉を落とす間隔（Attack の移動フレーム数）。 */
const SPARK_INTERVAL_FRAMES = 2

/** 1 回の散布で生成する火の粉の個数。 */
const SPARKS_PER_BURST = 2

/** 火の粉の寿命（秒）。短めにして通路が永続的に塞がらないようにする。 */
const SPARK_LIFETIME_SECONDS = 1.6

/** 火の粉の寸法の下限・上限（studs）。大小を混ぜて散らす。 */
const SPARK_SIZE_MIN = 0.7
const SPARK_SIZE_MAX = 1.9

/** 火の粉の色候補（黄〜橙〜赤）。Neon で自発光させ、燃えさしの見た目にする。 */
const SPARK_COLORS: ReadonlyArray<string> = ['#ffd24a', '#ff9000', '#ff5a1a']

/** 火の粉の発生位置の散らばり幅（studs）。軌道の周囲へランダムに撒く。 */
const SPARK_SCATTER = 2.6

/** 火の粉が 1 フレームに沈む量（studs）。燃えさしが舞い落ちる表現。 */
const SPARK_FALL_PER_FRAME = 0.06

/** スイッチ受信後にボスが落下するまでの待機秒数（Lua: wait(1)）。 */
const SWITCH_FALL_DELAY_SECONDS = 1

/** クリア判定の高さしきい値（Lua: PrimaryPart.Position.Y > -100）。 */
const CLEAR_FALL_THRESHOLD_Y = -100

/** RJF01 のシーン上のオブジェクト名（descriptor.objects[].name と一致）。 */
const OBJECT_NAME = {
  fall: 'Fall',
  resetSpeed: 'ResetSpeed',
  bossHead: 'Head',
  bossTorso: 'Torso',
  clearWall: 'ClearWall',
} as const

/** スイッチコードが届くメッセージ名（学習者コードの「スイッチ」送信）。 */
const SWITCH_MESSAGE = 'スイッチ'

/**
 * ボス戦 GUI の文言（実機 StarterGui/BossScreenGui/BossLocalScript）。
 * BossEvent → 開戦、SwitchEvent → 断末魔、ClearEvent → クリア（10 秒後に消す）。
 */
const BOSS_INITIAL_TEXT = 'ボクをたおすことができるかな？'
const BOSS_START_TEXT = 'よくここまできたな！くらえ！'
const BOSS_SWITCH_TEXT = 'うわああああ！'
const BOSS_CLEAR_TEXT = 'やられた・・・クリアおめでとう！'

/** クリア文言を消すまでの秒数（Lua: clear() の wait(10)）。 */
const CLEAR_TEXT_HIDE_SECONDS = 10

/**
 * Fall（黄色い床）の踏み抜きギミック。
 *
 * Lua: Touched で humanoid 接触かつ isFall==false のとき isFall=true → fall()。
 * fall(): Transparency を speed ずつ 1 まで上げる → CanCollide=false → wait(3) →
 * CanCollide=true・Transparency=0・isFall=false に戻す。
 *
 * isFall は factory closure 内に持ち、二重発火を防ぐ（実機の Touched 連打対策と同じ）。
 */
const createFallHandler = (): BehaviorHandler => {
  let isFall = false
  return async (ctx: BehaviorContext): Promise<void> => {
    if (isFall) {
      return
    }
    const id = findIdByName(ctx.world, OBJECT_NAME.fall)
    if (!id) {
      return
    }
    isFall = true
    try {
      // Transparency を speed ずつ 1 まで上げる（現在値は World から都度取得）。
      while (true) {
        const state = ctx.world.getState().get(id)
        if (!state || state.transparency >= 1) {
          break
        }
        ctx.world.setTransparency(
          id,
          state.transparency + FALL_TRANSPARENCY_STEP,
        )
        await ctx.wait(FRAME_SECONDS)
        if (ctx.signal.aborted) {
          return
        }
      }
      // 床を踏み抜けさせる（非衝突）→ 3 秒後に復帰。
      ctx.world.setCanCollide(id, false)
      await ctx.wait(FALL_RECOVER_SECONDS)
      if (ctx.signal.aborted) {
        return
      }
      ctx.world.setCanCollide(id, true)
      ctx.world.setTransparency(id, 0)
    } finally {
      isFall = false
    }
  }
}

/**
 * ボスのテレポート（PrimaryPart=Torso と Head を相対位置を保って X 移動）。
 * Lua は PrimaryPart のみ動かすが、Head は溶接（Weld）で追従するため、ここでは
 * Torso と Head の相対オフセットを維持して両方を同じ X へ寄せる。
 */
const teleportBossX = (world: EditablePreviewWorld, targetX: number): void => {
  const torso = findStateByName(world, OBJECT_NAME.bossTorso)
  if (!torso) {
    return
  }
  const head = findStateByName(world, OBJECT_NAME.bossHead)
  // Head は Torso との X オフセットを保ったまま移動する（相対位置維持）。
  if (head) {
    const offsetX = head.position.x - torso.position.x
    world.setPosition(head.id, {
      x: targetX + offsetX,
      y: head.position.y,
      z: head.position.z,
    })
  }
  world.setPosition(torso.id, {
    x: targetX,
    y: torso.position.y,
    z: torso.position.z,
  })
}

/**
 * Attack ブロックを 1 つ生成し、寿命ぶん +Z へ進めながらアバター接触を判定する。
 *
 * Lua: storage.Attack を clone して boss.PrimaryPart.Position + (0,2,5) に置き、
 * 毎フレーム +Z へ 1 進める。Touched(Humanoid) で Health=0、別スクリプトが 3 秒後に
 * 自壊する。プレビューでは火の玉を物理に干渉させない（canCollide/canTouch=false の
 * 純粋な演出物）ため、Touched の代わりにアバターとの AABB 重なりで接触を判定する。
 */
const spawnAttack = (
  ctx: BehaviorContext,
  spawnAt: Vec3,
  nextAttackId: (world: EditablePreviewWorld) => string,
): void => {
  const { world } = ctx
  // 既存 ID と衝突しない一意 ID を採る（addObject は重複 ID で throw する）。
  const id = nextAttackId(world)
  world.addObject({
    id,
    name: `Attack#${id}`,
    position: { ...spawnAt },
    size: { ...ATTACK_SIZE },
    color: ATTACK_COLOR,
    // rbxl 実測どおり球（Shape=Ball）+ 炎エフェクト（Fire 子インスタンス相当）で
    // 描画する。マテリアルは実測では Glass だが、火の玉の核の光は
    // Neon（自発光・色追従）の方が実機の印象に近いため Neon で近似する。
    material: 'Neon',
    shape: 'ball',
    fire: true,
    isStatic: true,
    // 演出と AABB ダメージ判定のみの存在。物理ではアバターを押したり
    // 進路を塞いだりせず、sensor 登録もしない（接触は AABB 側で判定）。
    canCollide: false,
    canTouch: false,
  })
  // fire-and-forget だが、runtime の例外処理（runBehavior の catch）の外で走るため
  // 未処理 Promise にならないようここで捕捉してログする。
  runAttackLoop(ctx, id, nextAttackId).catch((error: unknown) => {
    console.error(`[preview] Attack ブロックの実行中にエラー (id=${id})`, error)
  })
}

/**
 * Attack ブロックの移動・接触・自壊ループ。寿命（3 秒）ぶん毎フレーム +Z へ進め、
 * アバターと重なったら体力 0（物理側がリスポーンさせる）。
 * Attack はこのビヘイビアが所有する一時オブジェクトなので、寿命満了・abort・
 * 例外のどの経路でも finally で必ず destroy し、stop 後の残留を防ぐ。
 */
const runAttackLoop = async (
  ctx: BehaviorContext,
  id: string,
  nextId: (world: EditablePreviewWorld) => string,
): Promise<void> => {
  const { world } = ctx
  try {
    // 寿命はフレーム数ではなく実時間（wall-clock）で打ち切る。タブ非アクティブ等で
    // タイマーが間引かれると 1 フレームが数秒に伸び、フレーム数基準だと Attack が
    // 何十秒も残って同時生成数が膨らむため（ボスは約 3 秒ごとに追加生成する）。
    const deadlineMs = performance.now() + ATTACK_LIFETIME_SECONDS * 1000
    let frame = 0
    while (performance.now() < deadlineMs) {
      if (ctx.signal.aborted || !world.exists(id)) {
        return
      }
      world.moveBy(id, { x: 0, y: 0, z: ATTACK_STEP_Z })
      const block = world.getState().get(id)
      // 火の玉は物理に干渉させない（canCollide/canTouch=false）ため、Roblox の
      // Touched の代わりにアバターとの AABB 重なりで接触を判定する。
      if (block && touchesAvatar(world, block.position, block.size)) {
        world.setAvatarHealth(0)
      }
      // 軌道上に火の粉を散りばめる（一定フレームごと・複数個をランダムに散らす）。
      if (block && frame % SPARK_INTERVAL_FRAMES === 0) {
        for (let i = 0; i < SPARKS_PER_BURST; i += 1) {
          spawnSpark(ctx, block.position, nextId)
        }
      }
      frame += 1
      await ctx.wait(FRAME_SECONDS)
    }
  } finally {
    if (world.exists(id)) {
      world.destroy(id)
    }
  }
}

/**
 * 火の粉（燃えさし）を 1 つ生成する。火の玉の軌道の周囲へランダムに散らし、
 * 寿命の間その場に漂いながらゆっくり沈み、フェードアウトして消える。
 * 純粋な演出で、当たり判定は持たない（体力 0 は火の玉本体のみが与える）。
 */
const spawnSpark = (
  ctx: BehaviorContext,
  origin: Vec3,
  nextId: (world: EditablePreviewWorld) => string,
): void => {
  const { world } = ctx
  const id = nextId(world)
  const diameter =
    SPARK_SIZE_MIN + Math.random() * (SPARK_SIZE_MAX - SPARK_SIZE_MIN)
  const color = pickRandom(SPARK_COLORS)
  world.addObject({
    id,
    name: `Spark#${id}`,
    position: {
      x: origin.x + (Math.random() - 0.5) * SPARK_SCATTER * 2,
      y: origin.y + (Math.random() - 0.5) * SPARK_SCATTER,
      // 火の玉の少し後方（進行方向 +Z の逆側）に残すと尾を引いて見える。
      z: origin.z - 1 - Math.random() * 3,
    },
    size: { x: diameter, y: diameter, z: diameter },
    color,
    material: 'Neon',
    shape: 'ball',
    isStatic: true,
    // 火の粉は演出のみ。物理では干渉せず sensor 登録もしない。
    canCollide: false,
    canTouch: false,
  })
  runSparkLoop(ctx, id).catch((error: unknown) => {
    console.error(`[preview] 火の粉の実行中にエラー (id=${id})`, error)
  })
}

/**
 * 火の粉の滞留・消滅ループ。寿命の間ゆっくり沈みながら透明度を上げる。
 * 寿命満了・abort・例外のどの経路でも destroy する。
 */
const runSparkLoop = async (
  ctx: BehaviorContext,
  id: string,
): Promise<void> => {
  const { world } = ctx
  try {
    // Attack 同様、寿命は実時間で打ち切る（タイマー間引き時の大量残留防止）。
    const bornMs = performance.now()
    const lifetimeMs = SPARK_LIFETIME_SECONDS * 1000
    while (true) {
      const progress = (performance.now() - bornMs) / lifetimeMs
      if (progress >= 1) {
        return
      }
      if (ctx.signal.aborted || !world.exists(id)) {
        return
      }
      world.moveBy(id, { x: 0, y: -SPARK_FALL_PER_FRAME, z: 0 })
      // 寿命の進行に合わせてフェードアウトする（燃え尽きる表現）。
      world.setTransparency(id, progress)
      await ctx.wait(FRAME_SECONDS)
    }
  } finally {
    if (world.exists(id)) {
      world.destroy(id)
    }
  }
}

/**
 * ボス AI ループ（Lua: NessieScript move()）。BossEvent 受信で 1 度だけ起動する。
 * Torso の現在 X から候補 {x-10, x, x+10} を作り、falling になるまで:
 *   ランダムに 1 つを選んで Head/Torso を X テレポート → Attack 生成 → wait(3)。
 *
 * falling フラグは RJF01 のビヘイビア集合で共有する（スイッチ受信で立つ）。
 */
const runBossAi = async (
  ctx: BehaviorContext,
  isFalling: () => boolean,
  nextAttackId: (world: EditablePreviewWorld) => string,
): Promise<void> => {
  const torso = findStateByName(ctx.world, OBJECT_NAME.bossTorso)
  if (!torso) {
    return
  }
  const baseX = torso.position.x
  const candidates = BOSS_X_OFFSETS.map((offset) => baseX + offset)
  while (!isFalling() && !ctx.signal.aborted) {
    const targetX = pickRandom(candidates)
    teleportBossX(ctx.world, targetX)
    const current = findStateByName(ctx.world, OBJECT_NAME.bossTorso)
    if (current) {
      spawnAttack(
        ctx,
        {
          x: current.position.x + ATTACK_SPAWN_OFFSET.x,
          y: current.position.y + ATTACK_SPAWN_OFFSET.y,
          z: current.position.z + ATTACK_SPAWN_OFFSET.z,
        },
        nextAttackId,
      )
    }
    await ctx.wait(BOSS_CYCLE_SECONDS)
  }
}

/**
 * RJF01 の環境ビヘイビアを生成する。プレイ開始毎に新規生成し、isFall / bossStarted /
 * falling などの内部状態をリセットする。
 */
/** ボス戦 GUI の実機デザイン（BossScreenGui/TextLabel の実測値）。 */
const BOSS_HUD_STYLE: HudStyle = {
  background: '#005500',
  color: '#FFFF7F',
  border: '#000000',
  borderWidth: 5,
  wide: true,
}

export const createRjf01Behaviors = (): MissionBehaviors => {
  // ボス AI を 1 度だけ起動するためのフラグ（Lua: event.Event:Once(move)）。
  let bossStarted = false
  // ボス落下中フラグ（Lua: NessieScript の falling）。スイッチ受信で true。
  let falling = false
  // Attack ブロックの一意 ID 採番（採番値は既存 ID 衝突回避にのみ使う）。
  const nextAttackId = (world: EditablePreviewWorld): string =>
    nextTempId(world, '__rjf01_attack')

  const onTouchCharacter = new Map<string, BehaviorHandler>()

  // Fall（黄色い床）: 踏み抜きギミック。
  onTouchCharacter.set(OBJECT_NAME.fall, createFallHandler())

  // ResetSpeed: 歩行速度を 16 に戻し、初回のみボス AI を起動する
  // （Lua: SpeedResetScript + BossStartScript が同じ ResetSpeed の Touched に紐づく）。
  // ボス AI を await で返し、runtime の runBehavior が例外を捕捉できるようにする。
  onTouchCharacter.set(OBJECT_NAME.resetSpeed, (ctx) => {
    ctx.world.setAvatarWalkSpeed(RESET_WALK_SPEED)
    if (bossStarted) {
      return
    }
    bossStarted = true
    // Lua: replicated.BossEvent:FireAllClients() → BossScreenGui に開戦の文言。
    ctx.setHudText?.(BOSS_START_TEXT, BOSS_HUD_STYLE)
    // ボスエリア進入で BGM をボス戦曲（Thunderbolt）へ差し替える（ループ）。
    ctx.setBgm?.(BOSS_BGM_URL)
    return runBossAi(ctx, () => falling, nextAttackId)
  })

  // スイッチ受信: ボスを落下させる（Lua: storage["スイッチ"].Event → fall()）。
  // falling=true → wait(1) → Head/Torso のアンカーを外して物理で落下させる。
  // 多重受信（スイッチ連打）では最初の 1 回だけ落下処理を起動する。
  const onMessage = new Map<string, BehaviorHandler>()
  onMessage.set(SWITCH_MESSAGE, async (ctx) => {
    if (falling) {
      return
    }
    falling = true
    // Lua: replicated.SwitchEvent:FireAllClients() → 断末魔の文言。
    ctx.setHudText?.(BOSS_SWITCH_TEXT, BOSS_HUD_STYLE)
    await ctx.wait(SWITCH_FALL_DELAY_SECONDS)
    if (ctx.signal.aborted) {
      return
    }
    for (const name of [OBJECT_NAME.bossHead, OBJECT_NAME.bossTorso]) {
      const id = findIdByName(ctx.world, name)
      if (id) {
        ctx.world.setAnchored(id, false)
      }
    }
  })

  // クリア監視（Lua: NessieScript 末尾の常駐ループ）。Torso が落下しきったら
  // ClearWall を消す。1 フレームごとにポーリングする。
  // 先頭のハンドラは BossScreenGui の初期表示（TextLabel の初期 Text。実機は
  // プレイ開始時から緑帯で「ボクをたおすことができるかな？」が出ている）。
  const onPlay: ReadonlyArray<BehaviorHandler> = [
    showHudText(BOSS_INITIAL_TEXT, BOSS_HUD_STYLE),
    async (ctx) => {
      while (!ctx.signal.aborted) {
        const torso = findStateByName(ctx.world, OBJECT_NAME.bossTorso)
        if (torso && torso.position.y < CLEAR_FALL_THRESHOLD_Y) {
          const wallId = findIdByName(ctx.world, OBJECT_NAME.clearWall)
          if (wallId) {
            ctx.world.destroy(wallId)
          }
          // ClearWall が開いた（消えた）らクリア曲（ユメミライ）へ差し替える（ループ）。
          ctx.setBgm?.(CLEAR_BGM_URL)
          // Lua: ClearEvent:FireAllClients() → クリアの文言を出し、10 秒後に消す。
          ctx.setHudText?.(BOSS_CLEAR_TEXT, BOSS_HUD_STYLE)
          await ctx.wait(CLEAR_TEXT_HIDE_SECONDS)
          if (!ctx.signal.aborted) {
            ctx.setHudText?.(null)
          }
          return
        }
        await ctx.wait(FRAME_SECONDS)
      }
    },
  ]

  return { onPlay, onTouchCharacter, onMessage }
}
